import React, { useMemo, useState } from 'react';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  XMarkIcon,
  EyeIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import {
  useAuditoria,
  useAuditoriaTipos,
  useAuditoriaUsuariosDistintos,
  useAuditoriaKpis,
} from '../hooks/useAuditoria';
import { BadgeEmBreve, inputIris } from '../components/ui/iris';
import type { AuditoriaItem, AuditoriaFiltros } from '../types/auditoria';

// Cor do badge por prefixo do tipo (auth.*, cotacao.*, etc) — tokens Íris +
// sky/amber literais (com 7 categorias e poucas cores, há reuso intencional).
function corPorPrefixo(tipo: string): string {
  const prefixo = tipo.split('.')[0];
  switch (prefixo) {
    case 'auth':
      return 'bg-accent-soft text-accent-text';
    case 'usuario':
      return 'bg-danger-soft text-danger';
    case 'cotacao':
      return 'bg-warning-soft text-warning';
    case 'pedido':
      return 'bg-ok-soft text-ok';
    case 'sugestao':
      return 'bg-sky-500/15 text-sky-600 dark:text-sky-300';
    case 'reposicao':
      return 'bg-ok-soft text-ok';
    case 'agrupamento':
      return 'bg-warning-soft text-warning';
    default:
      return 'bg-surface-2 text-tx-2';
  }
}

const fmtData = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return '—';
  }
};

// Default ao abrir: últimas 24h. UI usa <input type="date"> que entrega
// "YYYY-MM-DD" — no backend isso é parseado como meia-noite local.
function isoOntem(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

const PAGE_SIZE = 50;

const AuditoriaPage: React.FC = () => {
  const [tipo, setTipo] = useState<string>('');
  const [usuario, setUsuario] = useState<string>('');
  const [periodoInicio, setPeriodoInicio] = useState<string>(isoOntem());
  const [periodoFim, setPeriodoFim] = useState<string>('');
  const [entidadeId, setEntidadeId] = useState<string>('');
  const [correlationId, setCorrelationId] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [itemSelecionado, setItemSelecionado] = useState<AuditoriaItem | null>(null);
  const [showFiltrosExtra, setShowFiltrosExtra] = useState(false);

  const filtros: AuditoriaFiltros = useMemo(() => ({
    tipo: tipo || undefined,
    usuario: usuario || undefined,
    periodoInicio: periodoInicio || undefined,
    periodoFim: periodoFim || undefined,
    entidadeId: entidadeId || undefined,
    correlationId: correlationId || undefined,
    page,
    pageSize: PAGE_SIZE,
  }), [tipo, usuario, periodoInicio, periodoFim, entidadeId, correlationId, page]);

  const { data, isLoading, isFetching, error, refetch } = useAuditoria(filtros);
  const { data: tipos = [] } = useAuditoriaTipos();
  const { data: usuarios = [] } = useAuditoriaUsuariosDistintos();
  const kpis = useAuditoriaKpis(isoOntem());

  const periodoInvalido = periodoFim !== '' && periodoInicio !== '' && periodoFim < periodoInicio;

  const filtrosExtraAtivos = !!(usuario || entidadeId || correlationId);

  const limparFiltros = () => {
    setTipo('');
    setUsuario('');
    setPeriodoInicio(isoOntem());
    setPeriodoFim('');
    setEntidadeId('');
    setCorrelationId('');
    setPage(1);
  };

  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;
  const inicio = data ? (data.currentPage - 1) * data.pageSize + 1 : 0;
  const fim = data ? Math.min(data.currentPage * data.pageSize, data.totalCount) : 0;

  return (
    // Mock Íris: conteúdo limitado a 1080px centralizado.
    <div className="flex h-full w-full max-w-[1280px] mx-auto flex-col gap-3">
      {/* Header (mock: sem ícone) */}
      <div>
        <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">Auditoria</h1>
        <p className="text-[13.5px] text-tx-3 mt-0.5">Histórico de eventos e atividades do sistema.</p>
      </div>

      {/* KPIs — Eventos/Logins reais (totalCount); Alterações/Alertas stub. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Eventos 24h" valor={kpis.eventos} />
        <KpiCard label="Logins 24h" valor={kpis.logins} />
        {/* TODO(backend): endpoint agregado de alterações/alertas — SISTEMA_PLANEJAMENTO.md */}
        <KpiCard label="Alterações" valor={undefined} stub />
        <KpiCard label="Alertas" valor={undefined} stub />
      </div>

      {/* Toolbar de filtros */}
      <div className="rounded-iris-lg border border-line bg-surface p-2.5">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Tipo</label>
            <select
              value={tipo}
              onChange={(e) => { setTipo(e.target.value); setPage(1); }}
              className={`min-w-[180px] ${inputIris} h-8 text-xs`}
            >
              <option value="">Todos os tipos</option>
              {tipos.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">De</label>
            <input
              type="date"
              value={periodoInicio}
              onChange={(e) => { setPeriodoInicio(e.target.value); setPage(1); }}
              className={`${inputIris} h-8 text-xs`}
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Até</label>
            <input
              type="date"
              value={periodoFim}
              onChange={(e) => { setPeriodoFim(e.target.value); setPage(1); }}
              className={`${inputIris} h-8 text-xs ${periodoInvalido ? '!border-danger' : ''}`}
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFiltrosExtra(v => !v)}
            className={`flex h-8 items-center gap-1.5 rounded-iris px-2.5 text-xs font-medium transition-colors ${
              showFiltrosExtra || filtrosExtraAtivos
                ? 'bg-accent-soft text-accent-text'
                : 'border border-line text-tx-2 hover:bg-surface-2'
            }`}
          >
            <FunnelIcon className="h-3.5 w-3.5" />
            Filtros
          </button>

          <button
            type="button"
            onClick={limparFiltros}
            className="h-8 rounded-iris border border-line px-2.5 text-xs text-tx-2 hover:bg-surface-2 transition-colors"
          >
            Limpar
          </button>

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex h-8 items-center gap-1 rounded-iris border border-line px-2.5 text-xs text-tx-2 hover:bg-surface-2 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>

        {showFiltrosExtra && (
          <div className="mt-2.5 pt-2.5 border-t border-line flex flex-wrap items-end gap-2">
            <div className="flex flex-col">
              <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Usuário</label>
              <select
                value={usuario}
                onChange={(e) => { setUsuario(e.target.value); setPage(1); }}
                className={`min-w-[140px] ${inputIris} h-8 text-xs`}
              >
                <option value="">Todos os usuários</option>
                {usuarios.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">ID da entidade</label>
              <input
                type="text"
                value={entidadeId}
                onChange={(e) => { setEntidadeId(e.target.value); setPage(1); }}
                placeholder="ex: 01|12345"
                className={`w-32 ${inputIris} h-8 text-xs`}
              />
            </div>

            <div className="flex flex-col">
              <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Correlation ID</label>
              <input
                type="text"
                value={correlationId}
                onChange={(e) => { setCorrelationId(e.target.value); setPage(1); }}
                placeholder="32 hex"
                className={`w-44 font-mono ${inputIris} h-8 text-[11px]`}
              />
            </div>
          </div>
        )}
      </div>

      {periodoInvalido && (
        <div className="rounded-iris border border-danger-border bg-danger-soft px-3 py-1.5 text-xs text-danger">
          Período inválido: data final é anterior à inicial.
        </div>
      )}

      {/* Tabela */}
      <div className="flex-1 overflow-auto rounded-iris-lg border border-line bg-surface">
        {isLoading ? (
          <p className="p-6 text-center text-sm text-tx-3">Carregando eventos...</p>
        ) : error ? (
          <p className="p-6 text-center text-sm text-danger">Erro ao carregar auditoria.</p>
        ) : !data || data.data.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-tx-3">
            <ShieldCheckIcon className="h-10 w-10 text-tx-3 opacity-50" />
            <span>Nenhum evento de auditoria encontrado neste filtro.</span>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-surface-2">
              <tr className="border-b border-line text-left text-[11px] font-semibold uppercase tracking-wide text-tx-3">
                <th className="py-2 px-2">Quando</th>
                <th className="py-2 px-2">Tipo</th>
                <th className="py-2 px-2">Usuário</th>
                <th className="py-2 px-2">Entidade</th>
                <th className="py-2 px-2">ID</th>
                <th className="py-2 px-2">Correlation</th>
                <th className="py-2 px-2">IP</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-line last:border-0 hover:bg-surface-2 transition-colors"
                >
                  <td className="whitespace-nowrap py-1.5 px-2 font-mono text-[11px] text-tx-2">
                    {fmtData(item.criadoEm)}
                  </td>
                  <td className="py-1.5 px-2">
                    <span className={`inline-block rounded-iris-sm px-2 py-0.5 text-[10px] font-semibold ${corPorPrefixo(item.tipo)}`}>
                      {item.tipo}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 font-medium text-tx">
                    {item.usuario || <span className="text-tx-3">—</span>}
                  </td>
                  <td className="py-1.5 px-2 text-tx-2">
                    {item.entidade || <span className="text-tx-3">—</span>}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[11px] text-tx-2">
                    {item.entidadeId || <span className="text-tx-3">—</span>}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-tx-3" title={item.correlationId ?? undefined}>
                    {item.correlationId ? item.correlationId.slice(0, 8) + '…' : '—'}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-tx-3">
                    {item.ip || '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <button
                      type="button"
                      onClick={() => setItemSelecionado(item)}
                      className="inline-flex items-center gap-1 rounded-iris-sm border border-line px-2 py-0.5 text-[11px] text-tx-2 hover:bg-surface-2 transition-colors"
                    >
                      <EyeIcon className="h-3 w-3" /> Ver JSON
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {data && data.totalCount > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-iris-lg border border-line bg-surface px-3 py-2 text-xs">
          <span className="text-tx-2">
            Mostrando {inicio}–{fim} de {totalCount.toLocaleString('pt-BR')} eventos
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className="rounded-iris-sm border border-line px-2 py-1 text-xs text-tx-2 hover:bg-surface-2 disabled:opacity-40"
            >
              Primeira
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-iris-sm border border-line px-2 py-1 text-xs text-tx-2 hover:bg-surface-2 disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="px-2 text-tx-2">
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-iris-sm border border-line px-2 py-1 text-xs text-tx-2 hover:bg-surface-2 disabled:opacity-40"
            >
              Próxima
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="rounded-iris-sm border border-line px-2 py-1 text-xs text-tx-2 hover:bg-surface-2 disabled:opacity-40"
            >
              Última
            </button>
          </div>
        </div>
      )}

      {/* Modal JSON */}
      {itemSelecionado && (
        <DialogoJson item={itemSelecionado} onFechar={() => setItemSelecionado(null)} />
      )}
    </div>
  );
};

const KpiCard: React.FC<{ label: string; valor?: number; stub?: boolean }> = ({ label, valor, stub }) => (
  <div className="rounded-iris-lg border border-line bg-surface p-4">
    <div className="text-2xl font-bold text-tx leading-tight">
      {valor != null ? valor.toLocaleString('pt-BR') : '—'}
    </div>
    <div className="mt-0.5 flex items-center gap-1.5 text-xs font-medium text-tx-2">
      {label}
      {stub && <BadgeEmBreve />}
    </div>
  </div>
);

const DialogoJson: React.FC<{ item: AuditoriaItem; onFechar: () => void }> = ({ item, onFechar }) => {
  // Tenta formatar o JSON; se falhar, mostra a string crua.
  const dadosFormatados = useMemo(() => {
    if (!item.dados) return '(sem payload)';
    try {
      return JSON.stringify(JSON.parse(item.dados), null, 2);
    } catch {
      return item.dados;
    }
  }, [item.dados]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] p-4"
      onClick={onFechar}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-3xl flex-col gap-3 rounded-iris-lg bg-surface border border-line p-4 shadow-iris-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-bold text-tx">{item.tipo}</h2>
            <p className="mt-0.5 text-xs text-tx-2">
              {item.usuario ?? 'sem usuário'} &middot; {new Date(item.criadoEm).toLocaleString('pt-BR')}
              {item.correlationId && (
                <> &middot; <span className="font-mono">correlation: {item.correlationId}</span></>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onFechar}
            className="rounded-iris-sm p-1 text-tx-3 hover:bg-surface-2"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {item.entidade && (
            <>
              <dt className="text-tx-3">Entidade</dt>
              <dd className="font-mono text-tx">{item.entidade}</dd>
            </>
          )}
          {item.entidadeId && (
            <>
              <dt className="text-tx-3">ID</dt>
              <dd className="font-mono text-tx">{item.entidadeId}</dd>
            </>
          )}
          {item.ip && (
            <>
              <dt className="text-tx-3">IP</dt>
              <dd className="font-mono text-tx">{item.ip}</dd>
            </>
          )}
        </dl>

        <div className="flex-1 overflow-auto rounded-iris border border-line bg-surface-2 p-3">
          <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-tx">
            {dadosFormatados}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default AuditoriaPage;
